import React, { useState, useRef, useEffect } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { ColumnDef } from "@tanstack/react-table";
import { useColumnVisibility, getDefaultVisibility } from "../../hooks/useColumnVisibility";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Calendar, Edit2, Download, Trash2, Plus, AlertTriangle, X, ChevronDown, Check } from "lucide-react";
import { GET_EVENTS_PAGINATED, DELETE_EVENT_MUTATION, GET_SYSTEM_SETTINGS, GET_ALL_EMPLOYEES, GET_EVENT_TYPES } from "../../graphql/queries";
import { EditEventDialog } from "./EditEventDialog";
import { CreateEventDialog } from "./CreateEventDialog";
import { DataTable } from "../ui/data-table";
import { usePageSize } from "../../hooks/usePageSize";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

interface Event {
  id: string;
  timestamp: string;
  note?: string;
  duration?: number;
  shiftId?: string;
  snapshotShiftName?: string;
  expectedShiftStart?: string;
  expectedShiftEnd?: string;
  employee: {
    id: string;
    firstName: string;
    lastName: string;
    department?: string;
  };
  eventType: {
    id: string;
    name: string;
    code: string;
    color: string;
  };
}

interface EventsListProps {
  filterEmployeeId?: string;
  filterStartDate?: string;
  filterEndDate?: string;
  onClearFilters?: () => void;
}

export const EventsList: React.FC<EventsListProps> = ({
  filterEmployeeId,
  filterStartDate,
  filterEndDate,
  onClearFilters
}) => {
  const { pageSize: defaultPageSize } = usePageSize();
  const [selectedEventTypeIds, setSelectedEventTypeIds] = useState<string[]>([]);
  const [eventTypeDropdownOpen, setEventTypeDropdownOpen] = useState(false);
  const eventTypeDropdownRef = useRef<HTMLDivElement>(null);
  const [startDate, setStartDate] = useState(
    filterStartDate || new Date().toISOString().split("T")[0]
  );
  const [endDate, setEndDate] = useState(
    filterEndDate || new Date().toISOString().split("T")[0]
  );
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(filterEmployeeId || "");
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: defaultPageSize,
  });
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  // Click-outside handler for event type dropdown
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (eventTypeDropdownRef.current && !eventTypeDropdownRef.current.contains(e.target as Node)) {
        setEventTypeDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch paginated events
  const { data, loading, error, refetch } = useQuery(GET_EVENTS_PAGINATED, {
    variables: {
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      employeeId: selectedEmployeeId || undefined,
      eventTypeIds: selectedEventTypeIds.length > 0 ? selectedEventTypeIds : undefined,
      startDate: startDate ? new Date(startDate + 'T00:00:00.000Z') : undefined,
      endDate: endDate ? new Date(endDate + 'T23:59:59.999Z') : undefined,
    },
    fetchPolicy: "network-only",
  });

  // Load system settings for duration limits
  const { data: settingsData } = useQuery<{
    systemSettings: {
      workDurationLimitMinutes: number;
      breakDurationLimitMinutes: number;
      smokeDurationLimitMinutes: number;
    };
  }>(GET_SYSTEM_SETTINGS, {
    fetchPolicy: 'cache-first',
  });

  // Load all employees for filter dropdown
  const { data: employeesData } = useQuery(GET_ALL_EMPLOYEES, {
    fetchPolicy: "cache-first",
  });

  // Load event types for filter dropdown
  const { data: eventTypesData } = useQuery(GET_EVENT_TYPES, {
    fetchPolicy: "cache-first",
  });

  const [deleteEvent, { loading: deleteLoading }] = useMutation(DELETE_EVENT_MUTATION, {
    onCompleted: () => {
      refetch();
    },
    onError: (error) => {
      console.error("Błąd przy usuwaniu wydarzenia:", error.message);
    },
  });

  const handleDeleteEvent = (eventId: string) => {
    deleteEvent({ variables: { id: eventId } });
  };

  const handleEditEvent = (event: Event) => {
    setEditingEvent(event);
    setIsEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setIsEditDialogOpen(false);
    setEditingEvent(null);
    refetch();
  };

  const handleClearFilters = () => {
    setSelectedEventTypeIds([]);
    setSelectedEmployeeId(""); // Will be displayed as "all" in the select
    const today = new Date().toISOString().split("T")[0];
    setStartDate(today);
    setEndDate(today);
    if (onClearFilters) {
      onClearFilters();
    }
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}min`;
    }
    return `${mins}min`;
  };

  const isDurationExceeded = (event: Event): boolean => {
    if (!event.duration || !settingsData?.systemSettings) return false;

    const settings = settingsData.systemSettings;
    const eventCode = event.eventType.code;

    switch (eventCode) {
      case 'BREAK_END':
        return event.duration > settings.breakDurationLimitMinutes;
      case 'SMOKE_END':
        return event.duration > settings.smokeDurationLimitMinutes;
      default:
        return false;
    }
  };

  const exportEvents = () => {
    const events = data?.eventsPaginated?.events || [];
    const csvContent = [
      ['Data', 'Czas', 'Pracownik', 'Dział', 'Zdarzenie', 'Czas trwania', 'Notatka'],
      ...events.map((event: Event) => [
        new Date(event.timestamp).toLocaleDateString('pl-PL', { timeZone: 'Europe/Warsaw' }),
        new Date(event.timestamp).toLocaleTimeString('pl-PL', {
          timeZone: 'Europe/Warsaw',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        }),
        `${event.employee.lastName} ${event.employee.firstName}`,
        event.employee.department || '',
        event.eventType.name,
        event.duration ? formatDuration(event.duration) : '',
        event.note || ''
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `wydarzenia_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // Define table columns
  const columns: ColumnDef<Event>[] = [
    {
      accessorKey: "timestamp",
      header: "Data i godzina",
      cell: ({ row }) => {
        const date = new Date(row.original.timestamp);
        const duration = row.original.duration;
        const isExceeded = isDurationExceeded(row.original);
        return (
          <div className={`min-w-[140px] ${isExceeded ? 'border-l-4 border-l-red-600 dark:border-l-red-500 pl-2 -ml-2' : ''}`}>
            <div>{date.toLocaleString("pl-PL", { timeZone: "Europe/Warsaw" })}</div>
            {duration !== null && duration !== undefined && (
              <div className={`flex items-center gap-1 text-xs font-semibold mt-1 ${isExceeded ? 'text-red-600 dark:text-red-500' : 'text-primary'}`}>
                {isExceeded && <AlertTriangle className="h-3 w-3" />}
                <span>{formatDuration(duration)}</span>
              </div>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "employee",
      header: "Pracownik",
      cell: ({ row }) => (
        <div className="font-medium">
          {row.original.employee.lastName} {row.original.employee.firstName}
        </div>
      ),
    },
    {
      accessorKey: "employee.department",
      header: "Dział",
      cell: ({ row }) => row.original.employee.department || "-",
    },
    {
      accessorKey: "eventType",
      header: "Typ zdarzenia",
      cell: ({ row }) => {
        const { eventType, snapshotShiftName, expectedShiftStart, expectedShiftEnd } = row.original;
        const isWorkStart = eventType.code === 'WORK_START';
        const shiftInfo = isWorkStart && snapshotShiftName
          ? ` — ${snapshotShiftName}${expectedShiftStart && expectedShiftEnd ? ` (${expectedShiftStart}–${expectedShiftEnd})` : ''}`
          : '';
        return (
          <div className="flex items-center space-x-2">
            <div
              className="w-3 h-3 rounded-full flex-shrink-0"
              style={{ backgroundColor: eventType.color }}
            />
            <Badge variant="outline">{eventType.name}{shiftInfo}</Badge>
          </div>
        );
      },
    },
    {
      accessorKey: "note",
      header: "Notatka",
      cell: ({ row }) => (
        <div className="text-sm text-muted-foreground max-w-[200px] truncate">
          {row.original.note || "-"}
        </div>
      ),
    },
    {
      id: "actions",
      header: "Akcje",
      enableHiding: false,
      cell: ({ row }) => (
        <div className="flex space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleEditEvent(row.original)}
          >
            <Edit2 className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleDeleteEvent(row.original.id)}
            disabled={deleteLoading}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      ),
    },
  ];

  const eventsDefaults = React.useMemo(() => getDefaultVisibility(columns), [columns]);
  const { columnVisibility, setColumnVisibility, resetToDefaults } = useColumnVisibility('events', eventsDefaults);

  const events = data?.eventsPaginated?.events || [];
  const totalPages = data?.eventsPaginated?.totalPages || 0;

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-600 mb-2">Błąd podczas ładowania wydarzeń</p>
          <p className="text-gray-600 text-sm">
            {error.message.includes('Unauthorized') ? 'Brak autoryzacji - zaloguj się ponownie' : error.message}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-foreground">
            Wydarzenia
          </h2>
          <p className="text-muted-foreground mt-2">
            Przeglądaj wszystkie zarejestrowane wydarzenia
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button onClick={() => setIsCreateDialogOpen(true)} className="flex items-center space-x-2">
            <Plus className="w-4 h-4" />
            <span>Dodaj wydarzenie</span>
          </Button>
          <Button onClick={exportEvents} className="flex items-center space-x-2" variant="outline">
            <Download className="w-4 h-4" />
            <span>Eksportuj CSV</span>
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Filtry</CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={handleClearFilters}
              className="flex items-center space-x-1"
            >
              <X className="w-4 h-4" />
              <span>Wyczyść</span>
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Date Range */}
            <div>
              <Label htmlFor="startDate">Data od</Label>
              <div className="relative">
                <Calendar className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="startDate"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="endDate">Data do</Label>
              <div className="relative">
                <Calendar className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="endDate"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Employee Filter */}
            <div>
              <Label htmlFor="employeeFilter">Pracownik</Label>
              <Select value={selectedEmployeeId || "all"} onValueChange={(value) => setSelectedEmployeeId(value === "all" ? "" : value)}>
                <SelectTrigger id="employeeFilter">
                  <SelectValue placeholder="Wszyscy" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Wszyscy pracownicy</SelectItem>
                  {employeesData?.employees
                    ?.slice()
                    .sort((a: any, b: any) => a.lastName.localeCompare(b.lastName, 'pl') || a.firstName.localeCompare(b.firstName, 'pl'))
                    .map((employee: any) => (
                      <SelectItem key={employee.id} value={employee.id}>
                        {employee.lastName} {employee.firstName}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            {/* Event Type Filter */}
            <div ref={eventTypeDropdownRef} className="relative">
              <Label htmlFor="eventTypeFilter">Typ zdarzenia</Label>
              <button
                type="button"
                id="eventTypeFilter"
                onClick={() => setEventTypeDropdownOpen(!eventTypeDropdownOpen)}
                className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              >
                <span className="truncate text-left">
                  {selectedEventTypeIds.length === 0
                    ? 'Wszystkie typy'
                    : selectedEventTypeIds.length === 1
                      ? eventTypesData?.eventTypes?.find((et: any) => et.id === selectedEventTypeIds[0])?.name || '1 typ'
                      : `${selectedEventTypeIds.length} typów`}
                </span>
                <div className="flex items-center gap-1">
                  {selectedEventTypeIds.length > 0 && (
                    <span
                      role="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedEventTypeIds([]);
                      }}
                      className="rounded-sm hover:bg-accent p-0.5"
                    >
                      <X className="h-3 w-3 text-muted-foreground" />
                    </span>
                  )}
                  <ChevronDown className="h-4 w-4 opacity-50" />
                </div>
              </button>
              {eventTypeDropdownOpen && (
                <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover p-1 shadow-md">
                  {eventTypesData?.eventTypes
                    ?.slice()
                    .sort((a: any, b: any) => a.order - b.order)
                    .map((eventType: any) => (
                      <button
                        key={eventType.id}
                        type="button"
                        onClick={() => {
                          setSelectedEventTypeIds(prev =>
                            prev.includes(eventType.id)
                              ? prev.filter((id: string) => id !== eventType.id)
                              : [...prev, eventType.id]
                          );
                        }}
                        className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent cursor-pointer"
                      >
                        <div className={`flex h-4 w-4 items-center justify-center rounded-sm border ${selectedEventTypeIds.includes(eventType.id) ? 'bg-primary border-primary' : 'border-muted-foreground/30'}`}>
                          {selectedEventTypeIds.includes(eventType.id) && <Check className="h-3 w-3 text-primary-foreground" />}
                        </div>
                        <div
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={{ backgroundColor: eventType.color }}
                        />
                        {eventType.name}
                      </button>
                    ))}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Events Table */}
      <Card>
        <CardHeader>
          <CardTitle>Lista wydarzeń</CardTitle>
          <CardDescription>
            Znaleziono {data?.eventsPaginated?.total || 0} wydarzeń
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={events}
            pageCount={totalPages}
            pagination={pagination}
            onPaginationChange={setPagination}
            loading={loading}
            manualPagination={true}
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={setColumnVisibility}
            onResetColumnVisibility={resetToDefaults}
          />
        </CardContent>
      </Card>

      <EditEventDialog
        event={editingEvent}
        open={isEditDialogOpen}
        onClose={handleCloseEditDialog}
      />

      <CreateEventDialog
        open={isCreateDialogOpen}
        onClose={() => {
          setIsCreateDialogOpen(false);
          refetch();
        }}
      />
    </div>
  );
};
